import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactElement,
  type ReactNode,
} from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ImagePreview component props
 */
interface ImagePreviewProps {
  thumbnail: string | null | undefined;
  previewImages?: string[];
  alt?: string;
  interval?: number;
  maxImages?: number;
  className?: string;
  children?: ReactNode;
}

/**
 * Image loaded state tracking
 */
type ImagesLoadedState = Record<number, boolean>;

/**
 * Helper to get full image URL
 */
const getImageUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith("/static/")) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

/**
 * ImagePreview Component
 *
 * Displays a thumbnail that cycles through preview images on hover.
 * Used in story cards to show a slideshow of page images.
 */
const ImagePreview = ({
  thumbnail,
  previewImages = [],
  alt = "Preview",
  interval = 1500,
  maxImages = 6,
  className = "",
  children,
}: ImagePreviewProps): ReactElement => {
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [imagesLoaded, setImagesLoaded] = useState<ImagesLoadedState>({});
  const [isTouchDevice, setIsTouchDevice] = useState<boolean>(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Detect touch device on mount
  useEffect(() => {
    const checkTouchDevice = (): boolean => {
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    };
    setIsTouchDevice(checkTouchDevice());
  }, []);

  // Limit preview images to maxImages
  const limitedPreviewImages = previewImages.slice(0, maxImages);
  const hasPreviewImages = limitedPreviewImages.length > 0;

  // Get the current image to display
  const getCurrentImage = useCallback((): string | null => {
    if (!isHovering || !hasPreviewImages) {
      return getImageUrl(thumbnail);
    }
    return getImageUrl(limitedPreviewImages[currentIndex]);
  }, [isHovering, hasPreviewImages, thumbnail, limitedPreviewImages, currentIndex]);

  // Preload images when hovering starts
  useEffect(() => {
    if (isHovering && hasPreviewImages) {
      // Preload all preview images
      limitedPreviewImages.forEach((imgUrl, idx) => {
        if (!imagesLoaded[idx]) {
          const img = new Image();
          img.onload = () => {
            setImagesLoaded((prev) => ({ ...prev, [idx]: true }));
          };
          const fullUrl = getImageUrl(imgUrl);
          if (fullUrl) {
            img.src = fullUrl;
          }
        }
      });
    }
  }, [isHovering, hasPreviewImages, limitedPreviewImages, imagesLoaded]);

  // Handle image cycling
  useEffect(() => {
    if (isHovering && hasPreviewImages && limitedPreviewImages.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % limitedPreviewImages.length);
      }, interval);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isHovering, hasPreviewImages, limitedPreviewImages.length, interval]);

  // Reset index when hover ends
  useEffect(() => {
    if (!isHovering) {
      setCurrentIndex(0);
    }
  }, [isHovering]);

  // Handle mouse enter with slight debounce (skip on touch devices)
  const handleMouseEnter = useCallback((): void => {
    // Skip hover preview on touch devices to avoid double-tap issues
    if (isTouchDevice) return;

    // Small delay to avoid triggering on quick pass-through
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(true);
    }, 100);
  }, [isTouchDevice]);

  // Handle mouse leave
  const handleMouseLeave = useCallback((): void => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovering(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const thumbnailUrl = getImageUrl(thumbnail);
  const showDots = isHovering && hasPreviewImages && limitedPreviewImages.length > 1;

  return (
    <div
      className={`image-preview-container ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Base thumbnail - always rendered */}
      {thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt={alt}
          className={`preview-image base ${
            !isHovering || !hasPreviewImages ? "active" : "inactive"
          }`}
        />
      )}

      {/* Preview images - rendered on hover */}
      {hasPreviewImages &&
        limitedPreviewImages.map((imgUrl, idx) => (
          <img
            key={idx}
            src={getImageUrl(imgUrl) || ""}
            alt={`${alt} - Page ${idx + 1}`}
            className={`preview-image overlay ${
              isHovering && currentIndex === idx ? "active" : "inactive"
            }`}
          />
        ))}

      {/* Progress dots */}
      {showDots && (
        <div className="preview-dots">
          {limitedPreviewImages.map((_, idx) => (
            <div
              key={idx}
              className={`preview-dot ${currentIndex === idx ? "active" : ""}`}
            />
          ))}
        </div>
      )}

      {/* Render any children (badges, overlays, etc.) */}
      {children}
    </div>
  );
};

export default ImagePreview;
